import type { Borrower } from 'api/btlApplicationForm/types';
import { getSortedIndividualsForDisplay } from 'api/btlApplicationForm/borrowerHelpers';
import {
  EvidenceProgress,
  EvidenceSection,
  EvidenceSectionNameMap,
  EvidenceTask,
  EvidenceTaskStatus,
} from 'api/brokerCases/types';

export interface ApplicantOption {
  id: string;
  name: string;
}

export { getSortedIndividualsForDisplay };

export function mapBorrowerToApplicantOptions(borrower?: Borrower): ApplicantOption[] {
  if (!borrower) {
    return [];
  }

  return getSortedIndividualsForDisplay(borrower).map(ind => ({
    id: ind.id,
    name: `${ind.firstName} ${ind.lastName}`.trim(),
  }));
}

export function getProgressPercent(completedCount: number, totalCount: number): number {
  return totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
}

export function getProgressFromSection(section: EvidenceSection): EvidenceProgress {
  const { tasks } = section;
  const completedTasks = tasks.filter(t => t.status === EvidenceTaskStatus.COMPLETED);

  return { totalCount: tasks.length, completedCount: completedTasks.length };
}

export function getProgressFromSections(sections: EvidenceSection[]): EvidenceProgress {
  return sections
    .filter(s => s.name !== EvidenceSectionNameMap.OTHER_DOCUMENTS)
    .map(s => getProgressFromSection(s))
    .reduce(
      (acc, curr) => ({
        totalCount: acc.totalCount + curr.totalCount,
        completedCount: acc.completedCount + curr.completedCount,
      }),
      { totalCount: 0, completedCount: 0 },
    );
}

export function getIndividualTasks(tasks: EvidenceTask[], individualId: string): EvidenceTask[] {
  const id = String(individualId);
  return tasks.filter(t => {
    const payloadId = t.payload.individualId;
    return payloadId != null && String(payloadId) === id;
  });
}

export function getCountOfPendingTasksPerApplicant(tasks: EvidenceTask[], individualId: string) {
  return tasks.filter(t => t.payload.individualId === individualId && t.status !== EvidenceTaskStatus.COMPLETED).length;
}

export function getApplicantDisplayNameForId(applicants: ApplicantOption[], individualId: string): string | undefined {
  return applicants.find(a => a.id === individualId)?.name;
}
